import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Tabs,
  Tab,
  ButtonGroup,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  card: {
    height: "100%",
  },
  metricValue: {
    fontSize: "1.6rem",
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  sectionTitle: {
    margin: theme.spacing(3, 0, 1),
    fontWeight: 600,
  },
  refreshBtn: {
    marginLeft: theme.spacing(2),
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
}));

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(2)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(2)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

const formatNumber = (n) => Number(n || 0).toLocaleString("pt-BR");

const formatDate = (d) =>
  d ? new Date(d).toLocaleString("pt-BR") : "—";

const formatCountdown = (ms) => {
  if (!ms || ms <= 0) return "—";
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (days > 0) return `${days}d ${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const bucketKey = (date, granularity) => {
  const d = new Date(date);
  if (granularity === "monthly") {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  if (granularity === "weekly") {
    const tmp = new Date(d);
    tmp.setHours(0, 0, 0, 0);
    tmp.setDate(tmp.getDate() - tmp.getDay());
    return tmp.toISOString().slice(0, 10);
  }
  return d.toISOString().slice(0, 10);
};

const bucketLabel = (key, granularity) => {
  if (granularity === "monthly") {
    const [y, m] = key.split("-");
    return `${m}/${y}`;
  }
  const d = new Date(key);
  return d.toLocaleDateString("pt-BR");
};

const aggregateHistory = (history, granularity) => {
  const buckets = new Map();
  for (const h of history) {
    const key = bucketKey(h.snapshotAt, granularity);
    const existing = buckets.get(key);
    if (!existing || new Date(h.snapshotAt) > new Date(existing.snapshotAt)) {
      buckets.set(key, h);
    }
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([key, h]) => ({
      label: bucketLabel(key, granularity),
      mensagens: Number(h.messagesCount),
      mbMensagens: +(Number(h.messagesBytes) / 1024 / 1024).toFixed(2),
      mbArquivos: +(Number(h.mediaFilesBytes) / 1024 / 1024).toFixed(2),
      contatos: Number(h.contactsCount),
      tickets: Number(h.ticketsCount),
    }));
};

const MetricCard = ({ label, value }) => {
  const classes = useStyles();
  return (
    <Card className={classes.card} variant="outlined">
      <CardContent>
        <Typography className={classes.metricLabel}>{label}</Typography>
        <Typography className={classes.metricValue}>{value}</Typography>
      </CardContent>
    </Card>
  );
};

const CompanyUsagePage = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isSuper = !!user?.super;
  const isAdmin = user?.profile === "admin" || isSuper;

  const [tab, setTab] = useState("own");
  const [granularity, setGranularity] = useState("daily");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [allCompanies, setAllCompanies] = useState([]);
  const [nextRunAt, setNextRunAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadOwn = async (pageArg = page, pageSizeArg = pageSize) => {
    try {
      const { data } = await api.get("/company-usage", {
        params: { page: pageArg + 1, pageSize: pageSizeArg },
      });
      setLatest(data.latest);
      setHistory(data.history || []);
      setChartData(data.chart || data.history || []);
      setTotalRows(data.pagination?.total || 0);
      setNextRunAt(data.nextRunAt ? new Date(data.nextRunAt) : null);
    } catch (err) {
      toastError(err);
    }
  };

  const loadAll = async () => {
    if (!isSuper) return;
    try {
      const { data } = await api.get("/company-usage/all");
      setAllCompanies(data || []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadOwn(), loadAll()]);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  const handleRefresh = async (companyId) => {
    setRefreshing(true);
    try {
      const path = companyId
        ? `/company-usage/refresh/${companyId}`
        : "/company-usage/refresh";
      await api.post(path);
      toast.success(i18n.t("companyUsage.page.refreshSuccess"));
      await Promise.all([loadOwn(), loadAll()]);
    } catch (err) {
      toastError(err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRecalculate = async (companyId) => {
    setRefreshing(true);
    try {
      await api.post(`/company-usage/recalculate/${companyId}`);
      toast.success(i18n.t("companyUsage.page.recalculateSuccess"));
      await loadAll();
    } catch (err) {
      toastError(err);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <MainContainer>
        <div className={classes.loadingBox}>
          <CircularProgress />
        </div>
      </MainContainer>
    );
  }

  const renderOwnTab = () => (
    <>
      <Typography variant="caption" color="textSecondary">
        {i18n.t("companyUsage.page.lastSnapshot")}: {formatDate(latest?.snapshotAt)}
      </Typography>

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={i18n.t("companyUsage.page.metrics.messages")}
            value={formatNumber(latest?.messagesCount)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={i18n.t("companyUsage.page.metrics.messagesSize")}
            value={formatBytes(latest?.messagesBytes)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={i18n.t("companyUsage.page.metrics.files")}
            value={formatNumber(latest?.mediaFilesCount)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={i18n.t("companyUsage.page.metrics.filesSize")}
            value={formatBytes(latest?.mediaFilesBytes)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={i18n.t("companyUsage.page.metrics.contacts")}
            value={formatNumber(latest?.contactsCount)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={i18n.t("companyUsage.page.metrics.contactsSize")}
            value={formatBytes(latest?.contactsBytes)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={i18n.t("companyUsage.page.metrics.tickets")}
            value={formatNumber(latest?.ticketsCount)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={i18n.t("companyUsage.page.metrics.ticketsSize")}
            value={formatBytes(latest?.ticketsBytes)}
          />
        </Grid>
      </Grid>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 16,
        }}
      >
        <Typography variant="h6" className={classes.sectionTitle} style={{ margin: 0 }}>
          {i18n.t("companyUsage.page.chart.title")}
        </Typography>
        <ButtonGroup size="small" color="primary">
          <Button
            variant={granularity === "daily" ? "contained" : "outlined"}
            onClick={() => setGranularity("daily")}
          >
            {i18n.t("companyUsage.page.chart.daily")}
          </Button>
          <Button
            variant={granularity === "weekly" ? "contained" : "outlined"}
            onClick={() => setGranularity("weekly")}
          >
            {i18n.t("companyUsage.page.chart.weekly")}
          </Button>
          <Button
            variant={granularity === "monthly" ? "contained" : "outlined"}
            onClick={() => setGranularity("monthly")}
          >
            {i18n.t("companyUsage.page.chart.monthly")}
          </Button>
        </ButtonGroup>
      </div>
      {chartData.length > 1 ? (
        <div style={{ width: "100%", height: 300, marginTop: 8 }}>
          <ResponsiveContainer>
            <LineChart
              data={aggregateHistory(chartData, granularity)}
              margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <ChartTooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="mensagens"
                stroke="#3f51b5"
                name={i18n.t("companyUsage.page.chart.messages")}
                dot={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="mbMensagens"
                stroke="#4caf50"
                name={i18n.t("companyUsage.page.chart.mbMessages")}
                dot={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="mbArquivos"
                stroke="#ff9800"
                name={i18n.t("companyUsage.page.chart.mbFiles")}
                dot={false}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="contatos"
                stroke="#9c27b0"
                name={i18n.t("companyUsage.page.metrics.contacts")}
                dot={false}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="tickets"
                stroke="#e91e63"
                name={i18n.t("companyUsage.page.metrics.tickets")}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {i18n.t("companyUsage.page.chart.empty")}
        </Typography>
      )}

      <Typography variant="h6" className={classes.sectionTitle}>
        {i18n.t("companyUsage.page.history.title")}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{i18n.t("companyUsage.page.history.colDate")}</TableCell>
            <TableCell align="right">{i18n.t("companyUsage.page.history.colMessages")}</TableCell>
            <TableCell align="right">{i18n.t("companyUsage.page.history.colMbMessages")}</TableCell>
            <TableCell align="right">{i18n.t("companyUsage.page.history.colFiles")}</TableCell>
            <TableCell align="right">{i18n.t("companyUsage.page.history.colMbFiles")}</TableCell>
            <TableCell align="right">{i18n.t("companyUsage.page.metrics.contacts")}</TableCell>
            <TableCell align="right">{i18n.t("companyUsage.page.metrics.contactsSize")}</TableCell>
            <TableCell align="right">{i18n.t("companyUsage.page.metrics.tickets")}</TableCell>
            <TableCell align="right">{i18n.t("companyUsage.page.metrics.ticketsSize")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((h) => (
            <TableRow key={h.id}>
              <TableCell>{formatDate(h.snapshotAt)}</TableCell>
              <TableCell align="right">{formatNumber(h.messagesCount)}</TableCell>
              <TableCell align="right">{formatBytes(h.messagesBytes)}</TableCell>
              <TableCell align="right">{formatNumber(h.mediaFilesCount)}</TableCell>
              <TableCell align="right">{formatBytes(h.mediaFilesBytes)}</TableCell>
              <TableCell align="right">{formatNumber(h.contactsCount)}</TableCell>
              <TableCell align="right">{formatBytes(h.contactsBytes)}</TableCell>
              <TableCell align="right">{formatNumber(h.ticketsCount)}</TableCell>
              <TableCell align="right">{formatBytes(h.ticketsBytes)}</TableCell>
            </TableRow>
          ))}
          {history.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} align="center">
                {i18n.t("companyUsage.page.history.empty")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalRows}
        page={page}
        onPageChange={(_, newPage) => {
          setPage(newPage);
          loadOwn(newPage, pageSize);
        }}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          setPageSize(newSize);
          setPage(0);
          loadOwn(0, newSize);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage={i18n.t("companyUsage.page.history.rowsPerPage")}
      />
    </>
  );

  const renderAdminTab = () => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{i18n.t("companyUsage.page.admin.colCompany")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.history.colMessages")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.history.colMbMessages")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.history.colFiles")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.history.colMbFiles")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.metrics.contacts")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.metrics.contactsSize")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.metrics.tickets")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.metrics.ticketsSize")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.admin.colSnapshot")}</TableCell>
          <TableCell align="right">{i18n.t("companyUsage.page.admin.colActions")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {allCompanies.map((row) => (
          <TableRow key={row.company.id}>
            <TableCell>{row.company.name}</TableCell>
            <TableCell align="right">{formatNumber(row.latest?.messagesCount)}</TableCell>
            <TableCell align="right">{formatBytes(row.latest?.messagesBytes)}</TableCell>
            <TableCell align="right">{formatNumber(row.latest?.mediaFilesCount)}</TableCell>
            <TableCell align="right">{formatBytes(row.latest?.mediaFilesBytes)}</TableCell>
            <TableCell align="right">{formatNumber(row.latest?.contactsCount)}</TableCell>
            <TableCell align="right">{formatBytes(row.latest?.contactsBytes)}</TableCell>
            <TableCell align="right">{formatNumber(row.latest?.ticketsCount)}</TableCell>
            <TableCell align="right">{formatBytes(row.latest?.ticketsBytes)}</TableCell>
            <TableCell align="right">{formatDate(row.latest?.snapshotAt)}</TableCell>
            <TableCell align="right">
              <Button
                size="small"
                disabled={refreshing}
                onClick={() => handleRefresh(row.company.id)}
              >
                {i18n.t("companyUsage.page.update")}
              </Button>
              <Button
                size="small"
                disabled={refreshing}
                onClick={() => handleRecalculate(row.company.id)}
              >
                {i18n.t("companyUsage.page.recalculate")}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("companyUsage.page.title")}</Title>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16 }}>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("companyUsage.page.nextRunIn")}:{" "}
            <strong>
              {nextRunAt ? formatCountdown(nextRunAt - now) : "—"}
            </strong>
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              disabled={refreshing}
              onClick={() => handleRefresh()}
            >
              {i18n.t("companyUsage.page.refreshNow")}
            </Button>
          )}
        </div>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {isSuper && (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            className={classes.tabs}
          >
            <Tab label={i18n.t("companyUsage.page.tabOwn")} value="own" />
            <Tab label={i18n.t("companyUsage.page.tabAll")} value="all" />
          </Tabs>
        )}

        {tab === "own" && renderOwnTab()}
        {tab === "all" && isSuper && renderAdminTab()}
      </Paper>
    </MainContainer>
  );
};

export default CompanyUsagePage;
